/**
 * Central manifest for the `ui/Modal` registry
 * (`registerModal`/`openModal`) — the modern counterpart to this
 * directory's legacy `PromptsContext`/`PromptManager` switch.
 *
 * Side-effect only: import this once (already done in `pages/_app.tsx`,
 * alongside `<ModalHost />`) so every registered type is dispatchable
 * app-wide. Add one entry to `ModalRegistry` and one `registerModal`
 * call per modal as it migrates onto the registry — keep this file a
 * flat list, no logic.
 */
import { registerModal } from '@ui/Modal';
import type { ExampleOnePayload } from '@ui/Modal/examples/exampleOneModal';
import type { ExampleTwoPayload } from '@ui/Modal/examples/exampleTwoModal';
import type { ImportModalProps } from 'components/Studio/ImportScriptV2/ImportModal';
import type { ImportModalSetupProps } from 'components/Studio/ImportScriptV2/ImportModalSetup';
import type { GuidedImportFlowProps } from 'components/Studio/ImportScriptV2/GuidedImportFlow';
import type { SmartImportOptOutNoticeProps } from 'components/Studio/ImportScriptV2/SmartImportOptOutNotice';

export type ModalRegistry = {
  'example-1': ExampleOnePayload;
  'example-2': ExampleTwoPayload;
  importModal: Omit<ImportModalProps, 'open' | 'close'>;
  importModalSetup: Omit<ImportModalSetupProps, 'open' | 'close'>;
  guidedImport: Omit<GuidedImportFlowProps, 'close'>;
  smartImportOptOut: Omit<SmartImportOptOutNoticeProps, 'close'>;
};

registerModal('example-1', () => import('@ui/Modal/examples/exampleOneModal'));
registerModal('example-2', () => import('@ui/Modal/examples/exampleTwoModal'));
registerModal(
  'importModal',
  () => import('../../Studio/ImportScriptV2/ImportModal')
);
registerModal(
  'importModalSetup',
  () => import('../../Studio/ImportScriptV2/ImportModalSetup')
);
registerModal(
  'guidedImport',
  () => import('../../Studio/ImportScriptV2/GuidedImportFlow')
);
registerModal(
  'smartImportOptOut',
  () => import('../../Studio/ImportScriptV2/SmartImportOptOutNotice')
);
